import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { getLanguageAbbrev, getLanguageDisplayName, type LanguageLike } from "@/utils/languageUtils";

type ContentLanguage = {
  languageId?: number;
};

type CompanyContentItem = {
  id: number;
  companyName?: string;
  contentTypeName?: string;
  contentTypeId?: number;
  contentType?: number;
  name?: string;
  languages?: ContentLanguage[];
};

type ContentImportModalProps = {
  isOpen: boolean;
  onClose?: () => void;
  companyId?: number | string;
  contentTypeId?: number | string | null;
  updateSpinner?: (isLoading: boolean) => void;
  onImported?: () => void | Promise<void>;
  zIndex?: number;
};

function resolveItemContentTypeId(item: CompanyContentItem) {
  return Number(item?.contentTypeId ?? item?.contentType ?? 0) || 0;
}

function normalizeContentItems(payload: unknown): CompanyContentItem[] {
  if (Array.isArray(payload)) return payload as CompanyContentItem[];
  if (payload && typeof payload === "object") {
    const maybeContents = (payload as { contents?: unknown }).contents;
    if (Array.isArray(maybeContents)) return maybeContents as CompanyContentItem[];
  }
  return [];
}

export default function ContentImportModal({
  isOpen,
  onClose,
  companyId,
  contentTypeId = null,
  updateSpinner,
  onImported,
  zIndex,
}: ContentImportModalProps) {
  const [items, setItems] = useState<CompanyContentItem[]>([]);
  const [languages, setLanguages] = useState<LanguageLike[]>([]);
  const [selectedContentIds, setSelectedContentIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);

  const targetTypeId = Number(contentTypeId || 0) || 0;

  function formatLanguageLabel(languageId: number) {
    const match = languages.find((lang) => Number(lang?.id) === Number(languageId));
    if (match) return getLanguageAbbrev(match) || getLanguageDisplayName(match);
    if (languageId === 1) return "eng";
    return `lang-${languageId}`;
  }

  const hasSelection = selectedContentIds.length > 0;

  const filteredItems = useMemo(() => {
    if (!targetTypeId) return items;
    return items.filter((item) => resolveItemContentTypeId(item) === targetTypeId);
  }, [items, targetTypeId]);

  useEffect(() => {
    if (!isOpen || !companyId) return;

    const loadCompanyContentsAsync = async () => {
      setIsLoading(true);
      setItems([]);
      setSelectedContentIds([]);
      if (typeof updateSpinner === "function") updateSpinner(true);

      try {
        const typeQuery = targetTypeId > 0 ? `?contentTypeId=${targetTypeId}` : "";
        const [contentsResp, languagesResp] = await Promise.all([
          RightTimeApi.getAsync(`/Company/${companyId}/Content/importSources${typeQuery}`),
          RightTimeApi.getAsync("/Languages"),
        ]);

        if (languagesResp?.ok && Array.isArray(languagesResp.jsonData)) {
          setLanguages(languagesResp.jsonData);
        } else {
          setLanguages([]);
        }

        const resp = contentsResp;
        if (resp?.ok) {
          setItems(normalizeContentItems(resp.jsonData));
          setSelectedContentIds([]);
        } else {
          toast.error("Failed to load company contents");
        }
      } catch (error) {
        toast.error("Error loading company contents");
      } finally {
        if (typeof updateSpinner === "function") updateSpinner(false);
        setIsLoading(false);
      }
    };

    loadCompanyContentsAsync();
  }, [isOpen, companyId, targetTypeId, updateSpinner]);

  function toggleSelection(contentId: number) {
    setSelectedContentIds((prev) =>
      prev.includes(contentId)
        ? prev.filter((id) => id !== contentId)
        : [...prev, contentId]
    );
  }

  async function importSelectedAsync() {
    if (!companyId || !hasSelection) return;

    setIsImporting(true);
    if (typeof updateSpinner === "function") updateSpinner(true);

    try {
      const payload = {
        TargetCompanyId: companyId,
        ContentIds: selectedContentIds,
      };

      const resp = await RightTimeApi.postAsync("/CompanyContent/Copy", payload);

      if (resp?.ok) {
        toast.success("Content imported successfully");
        await Promise.resolve(onImported?.());
        onClose?.();
      } else {
        toast.error("Failed to import selected content");
      }
    } catch (error) {
      toast.error("Error importing selected content");
    } finally {
      if (typeof updateSpinner === "function") updateSpinner(false);
      setIsImporting(false);
    }
  }

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={isImporting}
      >
        Cancel
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={importSelectedAsync}
        disabled={!hasSelection || isImporting}
      >
        {isImporting ? "Importing..." : "Import"}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Import Content"
      size="lg"
      className="companyContentImportModal"
      footer={footer}
      zIndex={zIndex}
    >
      <div className="tableWrapper dimensionTable scrollable">
        <table>
          <caption>
            <span className="title">Content Available for Import</span>
          </caption>
          <thead>
            <tr>
              <th></th>
              <th>Company Name</th>
              {!targetTypeId && <th>Content Type</th>}
              <th>Content Name</th>
              <th>Languages</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && filteredItems.length === 0 && (
              <tr>
                <td colSpan={targetTypeId ? 4 : 5} className="center">
                  No content available for import.
                </td>
              </tr>
            )}
            {filteredItems.map((item) => {
              const isSelected = selectedContentIds.includes(item.id);
              return (
                <tr
                  key={item.id}
                  onClick={() => toggleSelection(item.id)}
                  className={isSelected ? "selected" : ""}
                >
                  <td className="center">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelection(item.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td>{item.companyName}</td>
                  {!targetTypeId && <td>{item.contentTypeName}</td>}
                  <td>{item.name}</td>
                  <td>{(item.languages || []).map((l) => formatLanguageLabel(Number(l.languageId || 0))).join(", ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}
